import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { Button } from "components/ui/Button";
import { Checkbox as CustomCheckbox } from "components/ui/Checkbox";
// import { useFetchHealthMailLoginDetails } from "features/results/hooks/useHealthMailLoginDetails";
import useHealthMailSendPrescription from "features/medications/hooks/useHealthMailSendPrescription";
import { usePatientData } from "features/patients/hooks/usePatientData";
import useHealthMailContactsList from "features/results/hooks/useHealthMailContactsList";
import { onInvalidSubmit } from "utils/reportFormErrors";

import style from "./Table.module.css";

const formSchema = z.object({
  contactsIds: z
    .array(z.number())
    .min(1, "At least one contact must be selected"),
});

export const SendPrescriptionForm = ({
  onSuccess,
  onSkip,
  prescriptionId,
  patientId,
}) => {
  const queryClient = useQueryClient();
  const sendPrescription = useHealthMailSendPrescription({ onSuccess });
  const { data: contacts } = useHealthMailContactsList();
  // const { data: healthMailCredentials, isLoading: isLoadingCredentials } =
  //   useFetchHealthMailLoginDetails();
  const { data: patientData } = usePatientData(patientId);
  const [defaultPharmacy, setDefaultPharmacy] = useState(false);

  // const isHealthMailConfigured = !!healthMailCredentials;

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      contactsIds: [],
    },
  });

  useEffect(() => {
    if (!contacts || !patientData?.defaultPharmacyContactid) return;
    const contactExists = contacts.some(
      (c) => c.id === patientData.defaultPharmacyContactid
    );
    if (contactExists) {
      form.setValue("contactsIds", [patientData.defaultPharmacyContactid]);
      setDefaultPharmacy(true);
    }
  }, [contacts, patientData]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (values) => {
    sendPrescription.mutate({
      prescriptionId,
      patientId,
      contactsIds: values.contactsIds,
      defaultContact: defaultPharmacy,
    });

    queryClient.invalidateQueries();
  };

  return (
    <FormProvider {...form}>
      <form
        className={style.addLetterForm}
        onSubmit={form.handleSubmit(handleSubmit, onInvalidSubmit("SendPrescriptionForm"))}
      >
        {/* {!isLoadingCredentials && !isHealthMailConfigured && (
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: "12px",
              padding: "16px",
              background: "#fff8e1",
              border: "1px solid #f59e0b",
              borderRadius: "12px",
            }}
          >
            <AlertTriangle
              size={20}
              color="#d97706"
              style={{ flexShrink: 0, marginTop: "2px" }}
            />
            <div>
              <p
                style={{
                  fontWeight: 700,
                  color: "#92400e",
                  marginBottom: "4px",
                  fontSize: "14px",
                }}
              >
                HealthMail not configured
              </p>
              <p style={{ color: "#92400e", fontSize: "14px", margin: 0 }}>
                HealthMail credentials have not been set up for this practice.{" "}
                <Link
                  to={ROUTES.SETTINGS.PROFILE}
                  style={{
                    color: "#92400e",
                    fontWeight: 700,
                    textDecoration: "underline",
                  }}
                >
                  Configure HealthMail settings
                </Link>
              </p>
            </div>
          </div>
        )} */}

        <div>
          <label className={style.addLetterFormLabel}>Select Contacts</label>
          <Autocomplete
            multiple
            options={contacts || []}
            getOptionLabel={(option) => option.name}
            sx={{
              "& .MuiOutlinedInput-root": {
                border: "none !important",
                borderRadius: "12px",
              },
              "& .MuiOutlinedInput-input": {
                padding: "0 10px !important",
                height: "36px !important",
                outline: "none !important",
              },
              "& .MuiOutlinedInput-root:focus": {
                border: "1px solid var(--gray-400)",
                outline: "none !important",
              },
            }}
            onChange={(_, newValue) => {
              form.setValue(
                "contactsIds",
                newValue.map((contact) => contact.id)
              );
            }}
            value={(contacts || []).filter((contact) =>
              form.watch("contactsIds")?.includes(contact.id)
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search contacts"
                error={!!form.formState.errors.contactsIds}
              />
            )}
            fullWidth
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />
          {form.formState.errors.contactsIds && (
            <p
              style={{
                color: "var(--alert, #b70000)",
                fontSize: "14px",
                fontWeight: 400,
                lineHeight: "20px",
                marginTop: "5px",
              }}
            >
              {form.formState.errors.contactsIds.message}
            </p>
          )}
        </div>

        <div className={style.manualToggle}>
          <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <CustomCheckbox
              checked={defaultPharmacy ?? false}
              handleChange={(e) => setDefaultPharmacy(e.target.checked)}
            />
            Save as default for this patient
          </label>
        </div>

        <div
          style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}
        >
          {onSkip && (
            <Button variant="outlineGray" size="large" onClick={onSkip}>
              Skip
            </Button>
          )}
          <Button
            size="large"
            onClick={form.handleSubmit(handleSubmit, onInvalidSubmit("SendPrescriptionForm"))}
            disabled={sendPrescription.isPending}
          >
            {sendPrescription.isPending ? "Submitting..." : "Send"}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};
